import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Box,
  IconButton,
  Stack,
  Divider
} from '@mui/material'
import {
  Close as CloseIcon,
  Edit as EditIcon,
  SmokingRooms as SmokeIcon,
  AttachMoney as PriceIcon,
  CalendarToday as DateIcon,
  LocationOn as LocationIcon,
  Storage as HumidorIcon,
  Inventory2 as QuantityIcon
} from '@mui/icons-material'
import { Cigar } from '@store/api/cigarApi'
import { format } from 'date-fns'

interface CigarDetailsDialogProps {
  open: boolean
  onClose: () => void
  cigar: Cigar | null
  onEdit?: (cigar: Cigar) => void
  onStartSession?: (cigar: Cigar) => void
}

const CigarDetailsDialog: React.FC<CigarDetailsDialogProps> = ({
  open,
  onClose,
  cigar,
  onEdit,
  onStartSession
}) => {
  if (!cigar) return null

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'success'
      case 'smoked': return 'default'
      case 'traded': return 'info'
      case 'gifted': return 'warning'
      case 'damaged': return 'error'
      default: return 'default'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'available': return 'Available'
      case 'smoked': return 'Smoked'
      case 'traded': return 'Traded'
      case 'gifted': return 'Gifted'
      case 'damaged': return 'Damaged'
      default: return status
    }
  }

  const formatPrice = (price?: number | string) => {
    if (!price) return 'N/A'
    const numPrice = typeof price === 'string' ? parseFloat(price) : price
    if (isNaN(numPrice)) return 'N/A'
    return `$${numPrice.toFixed(2)}`
  }

  const formatDate = (date?: string) => {
    if (!date) return 'N/A'
    return format(new Date(date), 'MMM dd, yyyy')
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {cigar.brand.name} {cigar.cigarLine.name}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label={getStatusLabel(cigar.status)}
              color={getStatusColor(cigar.status) as any}
              size="small"
            />
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>
          {/* Primary Info */}
          <Grid item xs={12} sm={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Cigar Details
                </Typography>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Brand</Typography>
                    <Typography variant="body1" fontWeight="medium">{cigar.brand.name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Line</Typography>
                    <Typography variant="body1">{cigar.cigarLine.name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Vitola</Typography>
                    <Typography variant="body1">{cigar.vitola.name}</Typography>
                    {cigar.vitola.description && (
                      <Typography variant="body2" color="text.secondary">{cigar.vitola.description}</Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Size</Typography>
                    <Typography variant="body1">{cigar.length}&quot; × {cigar.ringGauge} Ring Gauge</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Quantity & Value */}
          <Grid item xs={12} sm={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Inventory
                </Typography>
                <Stack spacing={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <QuantityIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Quantity</Typography>
                      <Typography variant="body1" fontWeight="medium">{cigar.quantity}</Typography>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PriceIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Purchase Price</Typography>
                      <Typography variant="body1">{formatPrice(cigar.purchasePrice)}</Typography>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <DateIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Purchase Date</Typography>
                      <Typography variant="body1">{formatDate(cigar.purchaseDate)}</Typography>
                    </Box>
                  </Box>
                  {cigar.purchaseLocation && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocationIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">Purchase Location</Typography>
                        <Typography variant="body1">{cigar.purchaseLocation}</Typography>
                      </Box>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Storage */}
          <Grid item xs={12} sm={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Storage
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <HumidorIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Humidor</Typography>
                    <Typography variant="body1" fontWeight="medium">{cigar.humidor.name}</Typography>
                  </Box>
                </Box>
                {cigar.batchNumber && (
                  <Box mt={1}>
                    <Typography variant="body2" color="text.secondary">Batch Number</Typography>
                    <Typography variant="body1">{cigar.batchNumber}</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Tobacco Profile */}
          {(cigar.wrapperType || cigar.binderType || cigar.fillerType || cigar.strengthLevel) && (
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Tobacco Profile
                  </Typography>
                  <Stack spacing={0.5}>
                    {cigar.wrapperType && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Wrapper</Typography>
                        <Typography variant="body1">{cigar.wrapperType}</Typography>
                      </Box>
                    )}
                    {cigar.binderType && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Binder</Typography>
                        <Typography variant="body1">{cigar.binderType}</Typography>
                      </Box>
                    )}
                    {cigar.fillerType && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Filler</Typography>
                        <Typography variant="body1">{cigar.fillerType}</Typography>
                      </Box>
                    )}
                    {cigar.strengthLevel && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Strength</Typography>
                        <Typography variant="body1">{cigar.strengthLevel}</Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Notes */}
          {cigar.notes && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Notes
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    &ldquo;{cigar.notes}&rdquo;
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Dates */}
          <Grid item xs={12}>
            <Divider />
            <Box display="flex" gap={3} pt={1}>
              <Typography variant="caption" color="text.secondary">
                Added: {format(new Date(cigar.createdAt), 'MMM dd, yyyy')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Updated: {format(new Date(cigar.updatedAt), 'MMM dd, yyyy')}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {onStartSession && cigar.status === 'available' && (
          <Button
            onClick={() => onStartSession(cigar)}
            startIcon={<SmokeIcon />}
            variant="contained"
            color="primary"
          >
            Smoke
          </Button>
        )}
        {onEdit && (
          <Button
            onClick={() => onEdit(cigar)}
            startIcon={<EditIcon />}
            variant="outlined"
          >
            Edit
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default CigarDetailsDialog
